import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { classNames } from '../../helpers'
import {
  formatShortcut,
  toAriaKeyshortcuts
} from '../../helpers/keyboard-shortcut'
import NativeTooltip from '../native-tooltip'

import Bold from './icons/bold'
import Header from './icons/header'
import Italic from './icons/italic'
import ListOrdered from './icons/list-ordered'
import ListUnordered from './icons/list-unordered'
import PlaceholderMenu from './placeholder-menu'

const I18N = 'mail_template.edit_mail_template.'

// Tastenkürzel wie in StarterKit
const FORMAT_ACTIONS = [
  {
    id: 'bold',
    Icon: Bold,
    keys: ['Mod', 'B'],
    isActive: (editor) => editor.isActive('bold'),
    toggle: (chain) => chain.toggleBold()
  },
  {
    id: 'italic',
    Icon: Italic,
    keys: ['Mod', 'I'],
    isActive: (editor) => editor.isActive('italic'),
    toggle: (chain) => chain.toggleItalic()
  },
  {
    id: 'heading',
    Icon: Header,
    keys: ['Mod', 'Alt', '1'],
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
    toggle: (chain) => chain.toggleHeading({ level: 1 }),
    separatorBefore: true
  },
  {
    id: 'bullet_list',
    Icon: ListUnordered,
    keys: ['Mod', 'Shift', '8'],
    isActive: (editor) => editor.isActive('bulletList'),
    toggle: (chain) => chain.toggleBulletList(),
    separatorBefore: true
  },
  {
    id: 'ordered_list',
    Icon: ListOrdered,
    keys: ['Mod', 'Shift', '7'],
    isActive: (editor) => editor.isActive('orderedList'),
    toggle: (chain) => chain.toggleOrderedList()
  }
]

const Separator = () => (
  <div
    role='separator'
    aria-orientation='vertical'
    className='mx-1 h-5 w-px bg-gray-200'
  />
)

const ToolbarButton = ({
  id,
  label,
  shortcut,
  ariaKeyshortcuts,
  Icon,
  active,
  disabled,
  tabIndex,
  onFocus,
  onClick
}) => (
  <NativeTooltip
    text={`${label} ${shortcut}`}
    delay={300}
    position='bottom'
    className='relative flex'
  >
    <button
      type='button'
      data-toolbar-item={id}
      aria-label={label}
      aria-pressed={active}
      aria-keyshortcuts={ariaKeyshortcuts}
      disabled={disabled}
      tabIndex={tabIndex}
      onFocus={onFocus}
      // Ein Klick soll Fokus und Auswahl im Editor lassen
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={classNames(
        'flex h-8 w-8 items-center justify-center rounded text-gray-900',
        active ? 'bg-gray-200 hover:bg-gray-300' : 'hover:bg-gray-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600',
        'disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent'
      )}
    >
      <Icon className='h-5 w-5' />
    </button>
  </NativeTooltip>
)

ToolbarButton.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  shortcut: PropTypes.string.isRequired,
  ariaKeyshortcuts: PropTypes.string.isRequired,
  Icon: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  tabIndex: PropTypes.number.isRequired,
  onFocus: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired
}

const MenuBar = ({ editor, placeholders, getInsertRange }) => {
  const { t } = useTranslation()
  const toolbarRef = useRef(null)
  // Roving Tabindex, Tab springt einmal in die Toolbar, Pfeiltasten wandern darin
  const [focusedId, setFocusedId] = useState(null)

  if (!editor) {
    return null
  }

  const keyLabels = {
    ctrl: t(I18N + 'keys.ctrl'),
    alt: t(I18N + 'keys.alt'),
    shift: t(I18N + 'keys.shift')
  }

  const actions = FORMAT_ACTIONS.map((action) => ({
    ...action,
    label: t(I18N + 'toolbar.' + action.id),
    active: action.isActive(editor),
    disabled: !action.toggle(editor.can().chain().focus()).run()
  }))

  const enabledIds = [
    ...actions.filter(({ disabled }) => !disabled).map(({ id }) => id),
    ...(placeholders ? ['placeholders'] : [])
  ]
  const tabStopId = enabledIds.includes(focusedId) ? focusedId : enabledIds[0]

  const handleKeyDown = (event) => {
    // Im offenen Menü gehören die Pfeiltasten dem Menü
    if (event.target.closest('[role="menu"]')) return

    const items = [
      ...toolbarRef.current.querySelectorAll('[data-toolbar-item]')
    ].filter((item) => !item.disabled)
    const current = items.indexOf(event.target)
    if (current === -1) return

    const next = {
      ArrowRight: (current + 1) % items.length,
      ArrowLeft: (current - 1 + items.length) % items.length,
      Home: 0,
      End: items.length - 1
    }[event.key]
    if (next === undefined) return

    event.preventDefault()
    items[next].focus()
  }

  return (
    <div
      ref={toolbarRef}
      role='toolbar'
      aria-label={t(I18N + 'toolbar.label')}
      onKeyDown={handleKeyDown}
      className='flex flex-wrap items-center gap-1 rounded-t-xl border-b border-gray-200 bg-gray-50 px-3 py-2'
    >
      {actions.map(
        ({
          id,
          label,
          keys,
          Icon,
          active,
          disabled,
          toggle,
          separatorBefore
        }) => (
          <div key={id} className='flex items-center gap-1'>
            {separatorBefore && <Separator />}
            <ToolbarButton
              id={id}
              label={label}
              shortcut={formatShortcut(keys, { labels: keyLabels })}
              ariaKeyshortcuts={toAriaKeyshortcuts(keys)}
              Icon={Icon}
              active={active}
              disabled={disabled}
              tabIndex={tabStopId === id ? 0 : -1}
              onFocus={() => setFocusedId(id)}
              onClick={() => toggle(editor.chain().focus()).run()}
            />
          </div>
        )
      )}
      {placeholders && (
        <>
          <Separator />
          <PlaceholderMenu
            editor={editor}
            placeholders={placeholders}
            getInsertRange={getInsertRange}
            tabIndex={tabStopId === 'placeholders' ? 0 : -1}
            onFocus={() => setFocusedId('placeholders')}
          />
        </>
      )}
    </div>
  )
}

MenuBar.propTypes = {
  editor: PropTypes.object,
  placeholders: PropTypes.array,
  getInsertRange: PropTypes.func
}

MenuBar.defaultProps = {
  editor: null,
  placeholders: null,
  getInsertRange: () => null
}

export default MenuBar
